import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { SignUpSource } from '@/reducers/product-management';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './api';
import { Product, PaymentSource, Invoice } from './models';
import {
  filterAndMapVisibleProducts,
  ProductWithAdditionalInfo,
} from './components/utils';
import { isInApp } from '@arcsite/utils';
import { useSearchParams } from 'react-router-dom';
import message from '@/components/api-message';
import { getBillingSubscription } from './utils';
import { SubscripionInfo } from './context';

export const useIsWindowsSource = () => {
  const user = useSelector((state: RootState) => state.user);
  return user?.source === SignUpSource.WINDOWS;
};

export const useProducts = (params: {
  visible: boolean;
}): Product[] | ProductWithAdditionalInfo[] | undefined => {
  const freeVisible = useFreeTierVisible();
  const { visible } = params;
  const { data: products } = useQuery({
    queryKey: ['subscribe-products'],
    queryFn: api.getAllProducts,
  });
  if (!products) return undefined;

  if (visible) {
    return filterAndMapVisibleProducts(products, freeVisible);
  }
  return products;
};

export const useFreeTierVisible = () => {
  const [searchParams] = useSearchParams();
  // return false;
  return isInApp() && searchParams.get('free') === '1';
};

// Query key constants for consistency
export const SUBSCRIPTION_QUERY_KEYS = {
  subscription: ['subscription'] as const,
  creditCard: ['credit-card'] as const,
  upcomingInvoice: ['upcoming-invoice'] as const,
  failedInvoice: ['failed-invoice'] as const,
  billingSubscription: ['billing-subscription'] as const,
  stripeSetupIntent: ['stripe-setup-intent'] as const,
} as const;

/**
 * Hook to fetch the default credit card
 */
export function useCreditCard(enabled = true) {
  return useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEYS.creditCard,
    queryFn: async (): Promise<PaymentSource | null> => {
      const result = await api.getPaymentResources();
      return result.find(item => item.is_default === true) || null;
    },
    enabled,
  });
}

/**
 * Hook to fetch upcoming invoice
 * Only enabled when subscription is not canceled
 */
export function useUpcomingInvoice(enabled = true) {
  return useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEYS.upcomingInvoice,
    queryFn: async (): Promise<Invoice | null> => {
      try {
        return await api.getUpcomingInvoice();
      } catch (e: any) {
        // billing 取消 - code 109 means subscription canceled
        if (e.code === 109) {
          return null;
        }
        throw e;
      }
    },
    enabled,
  });
}

/**
 * Hook to fetch latest failed invoice
 */
export function useLatestFailedInvoice(enabled = true) {
  return useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEYS.failedInvoice,
    queryFn: async (): Promise<Invoice | null> => {
      try {
        return await api.getLatestFailedInvoice();
      } catch (e: any) {
        // 109 表示订阅已经取消
        if (e.code === 109) {
          // 订阅取消不需要再展示这个failed 的 invoice了
          return null;
        }
        throw e;
      }
    },
    enabled,
  });
}

/**
 * Mutation hook for retrying payment
 */
export function useRetryPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      return api.retryPay(invoiceId);
    },
    onSuccess: () => {
      // Invalidate related queries after successful retry
      queryClient.invalidateQueries({
        queryKey: SUBSCRIPTION_QUERY_KEYS.upcomingInvoice,
      });
      queryClient.invalidateQueries({
        queryKey: SUBSCRIPTION_QUERY_KEYS.failedInvoice,
      });

      // Also invalidate the main subscription queries
      queryClient.invalidateQueries({ queryKey: ['subscribe-products'] });
    },
    onError: err => {
      message.showErrorText(err.message);
    },
  });
}

/**
 * Hook to fetch billing subscription data
 */
export function useBillingSubscription(enabled = true) {
  return useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEYS.billingSubscription,
    queryFn: async (): Promise<SubscripionInfo & { isEmpty: boolean }> => {
      return getBillingSubscription();
    },
    enabled,
  });
}

/**
 * Hook to fetch Stripe setup intent client secret
 */
export function useStripeSetupIntent(enabled = true) {
  return useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEYS.stripeSetupIntent,
    queryFn: async (): Promise<string> => {
      return api.getStripeSetupIntentScrete();
    },
    enabled,
  });
}
